<template>
  <dashboard-layout>
    <div class="container">
      <h2 class="title">📁 ISO Logs & Tunnel Monitor</h2>

      <!-- Status + Control Section -->
      <div class="status-control">
        <!-- Telnet Status -->
        <div class="status-card" :class="tunnelStatusClass">
          <div class="status-icon">
            <i :class="tunnelStatusIcon"></i>
          </div>
          <div class="status-info">
            <h3>ISO → Interswitch Tunnel</h3>
            <p>{{ tunnelStatusMessage }}</p>
          </div>
        </div>

        <!-- Restart Service -->
        <div class="service-card">
          <h3>Service Control</h3>
          <p class="service-desc">
            Manage Windows Service: <b> Swwipe ISO Service</b>
          </p>
          <button @click="promptRestart" class="restart-btn">
            <i class="fas fa-sync-alt"></i> Restart Service
          </button>
        </div>
      </div>

      <!-- File List -->
      <div class="section">
        <h3 class="section-title">📂 ISO Log Files</h3>
        <div v-if="loading" class="loading">
          <i class="fas fa-spinner fa-spin"></i> Loading files...
        </div>
        <div v-else class="file-grid">
          <div v-for="file in files" :key="file.name" class="file-card">
            <div class="file-icon">
              <i class="fas fa-file-alt"></i>
            </div>
            <div class="file-info">
              <h3 class="file-name">{{ file.name }}</h3>
              <p class="file-size">{{ formatSize(file.size) }}</p>
              <p class="file-date">
                <b>Last Modified:</b> {{ formatDate(file.lastModified) }}
              </p>
            </div>
            <button @click="downloadFile(file.name)" class="download-btn">
              <i class="fas fa-download"></i> Download
            </button>
          </div>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import DashboardLayout from "@/components/DashboardLayout.vue";
import axios from "axios";

export default {
  name: "IsoLogsMonitor",
  components: { DashboardLayout },
  data() {
    return {
      files: [],
      loading: true,
      tunnelStatus: null, // "success", "error", "timeout"
      adminPassword: "SwwipeAdmin@2025", // Hardcoded check
    };
  },
  computed: {
    tunnelStatusClass() {
      return {
        success: this.tunnelStatus === "success",
        error: this.tunnelStatus === "error",
        timeout: this.tunnelStatus === "timeout",
      };
    },
    tunnelStatusIcon() {
      switch (this.tunnelStatus) {
        case "success":
          return "fas fa-check-circle";
        case "timeout":
          return "fas fa-clock";
        default:
          return "fas fa-times-circle";
      }
    },
    tunnelStatusMessage() {
      switch (this.tunnelStatus) {
        case "success":
          return "Connected";
        case "timeout":
          return "Connection Timed Out";
        case "error":
          return "Disconnected";
        default:
          return "Checking...";
      }
    },
  },
  methods: {
    async fetchFiles() {
      try {
        const response = await axios.get(this.$config.GETLOGSLIST_ENDPOINT);
        this.files = response.data;
      } catch (error) {
        console.error("Error fetching files:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchTunnelStatus() {
      try {
        const { data } = await axios.get(this.$config.TELNETCHECK_ENDPOINT);
        this.tunnelStatus = data.status;
      } catch (err) {
        this.tunnelStatus = "error";
      }
    },
    async restartService() {
      try {
        const { data } = await axios.post(this.$config.RESTARTSERVICE_ENDPOINT);
        alert(data.message || "Service restarted successfully.");
      } catch (error) {
        alert("Failed to restart service.");
        console.error(error);
      }
    },
    promptRestart() {
      const input = prompt("Enter admin password to restart the service:");
      if (input === this.adminPassword) {
        this.restartService();
      } else {
        alert("Invalid password. Action cancelled.");
      }
    },
    formatSize(bytes) {
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(1)} MB`;
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
    downloadFile(filename) {
      const link = document.createElement("a");
      link.href = `${this.$config.DOWNLOADLOG_ENDPOINT}/${filename}`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  },
  mounted() {
    this.fetchFiles();
    this.fetchTunnelStatus();
    // Refresh tunnel status every 10s
    setInterval(this.fetchTunnelStatus, 10000);
  },
};
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: auto;
  padding: 30px 20px;
}
.title {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}
.section {
  margin-top: 30px;
}
.section-title {
  font-size: 22px;
  margin-bottom: 20px;
}
.loading {
  text-align: center;
  font-size: 18px;
  color: #666;
}
.status-control {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}
.status-card {
  flex: 1;
  min-width: 280px;
  display: flex;
  align-items: center;
  padding: 15px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}
.status-card.success {
  border-left: 6px solid #2ecc71;
}
.status-card.error {
  border-left: 6px solid #e74c3c;
}
.status-card.timeout {
  border-left: 6px solid #f39c12;
}
.status-icon {
  font-size: 40px;
  margin-right: 15px;
}
.status-info h3 {
  margin: 0;
  font-size: 18px;
}
.status-info p {
  margin: 5px 0 0;
  color: #555;
}
.service-card {
  flex: 1;
  min-width: 280px;
  background: #fff;
  padding: 15px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.service-desc {
  color: #555;
  margin-bottom: 15px;
}
.restart-btn {
  background: #e74c3c;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  transition: background 0.3s ease;
}
.restart-btn:hover {
  background: #c0392b;
}
.file-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}
.file-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s ease;
}
.file-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-3px);
}
.file-icon {
  font-size: 40px;
  color: #3498db;
  margin-bottom: 15px;
}
.file-info {
  text-align: center;
  margin-bottom: 15px;
}
.file-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}
.file-size {
  font-size: 14px;
  color: #888;
}
.file-date {
  font-size: 12px;
  color: #aaa;
  margin-top: 5px;
}
.download-btn {
  background: #2ecc71;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  text-align: center;
  transition: background 0.3s ease;
}
.download-btn:hover {
  background: #27ae60;
}
</style>
